import { Button, Typography } from "@hellocarbo/lasagne";
import { Popover } from "@hellocarbo/lasagne";
import { use } from "react";
import { useTranslation } from "react-i18next";

import EllipseSvg from "@/assets/common/other_icons/ellipse.svg?react";
import { PortalContainerContext } from "@/contexts";
import { ChartData } from "@/types";
import { formatCarbon, getCarbonDisplayUnit } from "@/utils/carbon";

type CategoryDetailPopoverProps = {
  title: string;
  data: ChartData;
  total: number;
};

function CategoryDetailPopover({
  title,
  data,
  total,
}: CategoryDetailPopoverProps) {
  const { t } = useTranslation(["common", "formatter", "dashboard"]);
  const { container } = use(PortalContainerContext);
  const listUnit = getCarbonDisplayUnit(data.map((dataItem) => dataItem.value));

  return (
    <>
      <Popover
        title={title}
        hasCloseButton
        portalContainer={container as Element}
        side="top"
        sideOffset={-5}
        trigger={
          <Button
            shape="link"
            as="button"
            size="small"
            intent="secondary"
            disabled={total === 0}
          >
            {t("dashboard:consolidation.show")}
          </Button>
        }
      >
        <table className="border-spacing-y-3xs border-separate">
          <tbody>
            {data
              .filter((subcategory) => subcategory.value > 0)
              .map((subcategory) => {
                const { formattedCarbon, unit } = formatCarbon(
                  t,
                  subcategory.value,
                  listUnit
                );
                return (
                  <tr key={subcategory.name}>
                    <Typography
                      step="-1"
                      as="td"
                      className="pr-sm flex items-center"
                    >
                      <EllipseSvg
                        fill={subcategory.color}
                        className="mr-2xs size-2xs shrink-0"
                        role="img"
                        aria-label="Ellispe with subcategory color"
                      />
                      {subcategory.name}
                    </Typography>
                    <Typography step="-1" as="td" className="pr-xs text-center">
                      {t("formatter:percentage", {
                        value: subcategory.value / total,
                      })}
                    </Typography>
                    <Typography step="-1" as="td" className="text-right">
                      {formattedCarbon}
                      &nbsp;
                      <Typography
                        step="-2"
                        as="span"
                        className="text-xxs text-grayscale"
                      >
                        {t(`common:unit.${unit}`)}
                      </Typography>
                    </Typography>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </Popover>
    </>
  );
}

export default CategoryDetailPopover;
